import React, { useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import Upload from 'src/shared/components/upload/Upload';
import { Input } from 'src/shared/components/ui/input';
import { PrimaryButton } from 'src/shared/components/button/Button';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import useBoolean from 'src/shared/hooks/useBoolean';
import config from 'src/config';
import { FileUp as FileUploadOutlinedIcon, FileText as InsertDriveFileOutlinedIcon, X as CloseIcon } from 'lucide-react';

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type UploadState = 'idle' | 'selected' | 'uploading' | 'error';

type UploadFileModalProps = {
    onClose: () => void;
    onUpload: (files: File[]) => Promise<{ isSuccess: boolean; message: string }>;
    description: string;
};

type SelectedFile = {
    file: File;
    customName: string;
};

const UploadFileModal = ({ onClose, onUpload, description }: UploadFileModalProps) => {
    const [uploadState, setUploadState] = useState<UploadState>('idle');
    const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [sizeError, setSizeError] = useBoolean(false);

    const getFileNameWithoutExtension = (filename: string) => {
        const lastDotIndex = filename.lastIndexOf('.');
        return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
    };

    const getFileExtension = (filename: string) => {
        const lastDotIndex = filename.lastIndexOf('.');
        return lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) {
            return '0 Bytes';
        }

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const handleFileSelect = (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files;
        setErrorMessage('');

        // Check file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setSizeError.on();
            setUploadState('idle');
            return;
        }

        setSizeError.off();
        setSelectedFile({
            file,
            customName: getFileNameWithoutExtension(file.name),
        });
        setUploadState('selected');
    };

    const handleDeleteFile = () => {
        setSelectedFile(null);
        setUploadState('idle');
        setSizeError.off();
    };

    const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (selectedFile) {
            setSelectedFile({
                ...selectedFile,
                customName: e.target.value,
            });
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            return;
        }

        setUploadState('uploading');
        setUploadProgress(0);

        let progressInterval: number | undefined;
        try {
            progressInterval = window.setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        if (progressInterval !== undefined) {
                            clearInterval(progressInterval);
                        }
                        return prev;
                    }
                    return prev + 10;
                });
            }, 200);

            const fileExtension = getFileExtension(selectedFile.file.name);
            const newFileName = selectedFile.customName + fileExtension;
            const renamedFile = new File([selectedFile.file], newFileName, {
                type: selectedFile.file.type,
            });

            const { isSuccess, message } = await onUpload([renamedFile]);

            if (!isSuccess) {
                setErrorMessage(
                    `There was an error uploading your file.
                    ${message}
                    Please select another file or contact our support team if you need help.`
                );
                setSelectedFile(null);
                setUploadProgress(0);
                setSizeError.off();
                setUploadState('idle');
                return;
            }

            setUploadProgress(100);
            setTimeout(() => {
                onClose();
            }, 500);
        } catch (error) {
            const errorMessage = (error as Error).message;
            setErrorMessage(
                `There was an error uploading your file.
                ${errorMessage}
                Please select another file or contact our support team if you need help.`
            );
            setSelectedFile(null);
            setUploadProgress(0);
            setSizeError.off();
            setUploadState('idle');
        } finally {
            if (progressInterval !== undefined) {
                clearInterval(progressInterval);
            }
        }
    };

    return (
        <Modal onClose={onClose} dismissOnClickOutside={false}>
            <div className='w-[800px] max-w-[90vw]'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <div className='w-14 h-14 rounded-full border-2 border-teal-60 flex items-center justify-center'>
                        <FileUploadOutlinedIcon className='text-teal-60' sx={{ fontSize: 28 }} />
                    </div>
                    <h2 className='text-[28px] font-normal text-navy m-0'>Upload file</h2>
                </div>

                {/* Description */}
                <p className='text-base text-grey-80 mb-6 leading-relaxed'>{description}</p>

                {/* Requirements */}
                <ul className='list-disc pl-5 mb-6'>
                    <li className='text-sm text-grey-80 mb-2'>All file types accepted</li>
                    <li className='text-sm text-grey-80 mb-2'>Maximum file size: {MAX_FILE_SIZE_MB}MB</li>
                </ul>

                {/* Error Messages */}
                {sizeError && (
                    <ErrorMessage
                        message={
                            <>
                                <p className='font-medium mb-1'>This file is larger than {MAX_FILE_SIZE_MB}MB.</p>
                                <p className='mb-0'>
                                    Please select another file or contact our{' '}
                                    <a href={config.supportUrl} target='_blank' rel='noreferrer' className='underline'>
                                        support team
                                    </a>{' '}
                                    if you need help.
                                </p>
                            </>
                        }
                    />
                )}

                {errorMessage && <ErrorMessage message={<div className='whitespace-pre-line'>{errorMessage}</div>} />}

                {/* Upload States */}
                {uploadState === 'idle' && (
                    <div
                        className='mb-6'
                        style={
                            {
                                '--drop-zone-height': '160px',
                                '--drop-zone-width': '100%',
                                '--drop-zone-bg-color': 'rgb(246, 246, 246)',
                            } as React.CSSProperties
                        }
                    >
                        <Upload allowMultiple={false} onUpload={handleFileSelect} />
                    </div>
                )}

                {uploadState === 'selected' && selectedFile && (
                    <div className='mb-6'>
                        <div className='p-4 bg-grey-0 rounded-md flex items-center gap-4 mb-4'>
                            <InsertDriveFileOutlinedIcon className='text-grey-80' sx={{ fontSize: 48 }} />
                            <div className='flex-1'>
                                <p className='text-base font-medium text-navy mb-1'>{selectedFile.file.name}</p>
                                <p className='text-sm text-grey-60 mb-0'>{formatFileSize(selectedFile.file.size)}</p>
                            </div>
                            <button onClick={handleDeleteFile} className='text-teal-60 font-medium hover:underline'>
                                Delete
                            </button>
                        </div>

                        {/* File name input */}
                        <div>
                            <label className='block text-teal-60 text-sm font-medium mb-2'>File name</label>
                            <div className='flex items-center gap-2'>
                                <Input value={selectedFile.customName} onChange={handleFileNameChange} className='flex-1' />
                                <span className='text-grey-80 font-medium'>{getFileExtension(selectedFile.file.name)}</span>
                            </div>
                        </div>
                    </div>
                )}

                {uploadState === 'uploading' && selectedFile && (
                    <div className='mb-6 p-6 bg-grey-0 rounded-md'>
                        <p className='text-teal-60 font-medium mb-3'>Uploading:</p>
                        <div className='flex items-start gap-4'>
                            <InsertDriveFileOutlinedIcon className='text-grey-80' sx={{ fontSize: 48 }} />
                            <div className='flex-1'>
                                <p className='text-base font-medium text-navy mb-1'>
                                    {selectedFile.customName + getFileExtension(selectedFile.file.name)}
                                </p>
                                <p className='text-sm text-grey-60 mb-3'>{formatFileSize(selectedFile.file.size)}</p>
                                <div className='relative h-2 w-full overflow-hidden rounded-full bg-grey-20'>
                                    <div className='h-full bg-teal-60 transition-all' style={{ width: `${uploadProgress}%` }} />
                                </div>
                            </div>
                            <button onClick={handleDeleteFile} className='text-grey-60 hover:text-grey-80'>
                                <CloseIcon />
                            </button>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className='flex justify-end'>
                    <PrimaryButton
                        onClick={handleUpload}
                        disabled={uploadState !== 'selected'}
                        className='min-w-[140px] px-8 py-3 text-base disabled:opacity-40 disabled:cursor-not-allowed'
                    >
                        Upload
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default UploadFileModal;
